import { isIos, isStandalone } from "./install-platform";

export const pushSupport = {
  supported: "supported",
  needsInstall: "needs-install",
  unsupported: "unsupported",
};

export const detectPushSupport = (win = window) => {
  const nav = win.navigator;
  if (isIos(nav) && !isStandalone(win)) return pushSupport.needsInstall;
  const capable =
    Boolean(nav?.serviceWorker) && "PushManager" in win && "Notification" in win;
  return capable ? pushSupport.supported : pushSupport.unsupported;
};

export const notificationPermission = (win = window) =>
  win.Notification?.permission ?? "default";

// Old Safari only knows the callback form of requestPermission.
export const requestNotificationPermission = (win = window) =>
  new Promise((resolve) => {
    const result = win.Notification.requestPermission(resolve);
    if (result?.then) result.then(resolve, () => resolve("default"));
  });

export const urlBase64ToUint8Array = (base64) => {
  const padded = `${base64}${"=".repeat((4 - (base64.length % 4)) % 4)}`;
  const raw = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
};

const sameBytes = (buffer, bytes) => {
  if (!buffer) return false;
  const actual = new Uint8Array(buffer);
  return (
    actual.length === bytes.length &&
    actual.every((value, index) => value === bytes[index])
  );
};

export const toSubscriptionPayload = (subscription) => {
  const { endpoint, keys = {} } = subscription.toJSON();
  return { endpoint, keys: { p256dh: keys.p256dh, auth: keys.auth } };
};

export const getPushRegistration = async (nav = window.navigator) =>
  (await nav.serviceWorker?.getRegistration?.()) ?? null;

export const findSubscription = async (nav = window.navigator) => {
  const registration = await getPushRegistration(nav);
  return registration ? registration.pushManager.getSubscription() : null;
};

export const subscribePush = async (publicKey, nav = window.navigator) => {
  if (!(await getPushRegistration(nav))) return null;
  const registration = await nav.serviceWorker.ready;
  const applicationServerKey = urlBase64ToUint8Array(publicKey);
  const existing = await registration.pushManager.getSubscription();
  if (
    existing &&
    sameBytes(existing.options?.applicationServerKey, applicationServerKey)
  ) {
    return existing;
  }
  if (existing) await existing.unsubscribe();
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey,
  });
};

export const unsubscribePush = async (nav = window.navigator) => {
  const subscription = await findSubscription(nav);
  if (!subscription) return null;
  await subscription.unsubscribe();
  return subscription.endpoint;
};
